import React from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  Card,
  Box,
  Stack,
  IconButton,
  Chip,
  InputAdornment,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Page from 'src/components/Page';
import { financialSimulationService, CreateScenarioDTO } from 'src/services/financial-simulation.service';
import { addScenarioValidationSchema } from 'src/validations/admin/scenario.validation';

const PRESET_AMOUNTS = [25, 50, 75, 100, 150, 250, 500];

export default function AddScenario(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: createScenario, isPending: isLoading } = useMutation({
    mutationFn: (values: CreateScenarioDTO) => financialSimulationService.createScenario(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['scenarioOptions'] });
      queryClient.invalidateQueries({ queryKey: ['whammies-metrics'] });
      toast.success('Whammy scenario created successfully');
      navigate(-1);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create scenario');
    },
  });

  const formik = useFormik<CreateScenarioDTO>({
    initialValues: {
      title: '',
      amount: '' as any,
      description: '',
    },
    validationSchema: addScenarioValidationSchema,
    onSubmit: async (values) => {
      const formattedValues = {
        title: values.title.trim(),
        description: values.description.trim(),
        amount: typeof values.amount === 'string' ? parseFloat(values.amount) : Number(values.amount),
      };
      createScenario(formattedValues);
    },
  });

  const currentAmount = Number(formik.values.amount) || 0;

  return (
    <Page title="Create Whammy Scenario | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Top Navigation */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton
              size="small"
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.75,
                p: 0.75,
              }}
            >
              <Icon icon="solar:arrow-left-linear" width={18} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                Create Whammy Scenario
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Design a new simulation event or penalty scenario for scholars.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {/* Form Fields Card */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, fontSize: '0.95rem' }}>
                  Scenario Details
                </Typography>

                <Stack spacing={2}>
                  {/* Title Field */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Scenario Title *
                    </Typography>
                    <OutlinedInput
                      fullWidth
                      size="small"
                      name="title"
                      placeholder="e.g. Broken Laptop Screen, Unexpected Car Repair"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(formik.touched.title && formik.errors.title)}
                      disabled={isLoading}
                      sx={{ borderRadius: 1.75, fontSize: '0.8125rem', height: 38 }}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <FormHelperText error sx={{ ml: 0.5, mt: 0.25, fontSize: '0.6875rem' }}>
                        {formik.errors.title}
                      </FormHelperText>
                    )}
                  </Box>

                  {/* Amount Field */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Penalty Amount ($) *
                    </Typography>
                    <OutlinedInput
                      fullWidth
                      size="small"
                      name="amount"
                      type="number"
                      placeholder="0.00"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      startAdornment={<InputAdornment position="start"><span style={{ fontSize: '0.8125rem' }}>$</span></InputAdornment>}
                      error={Boolean(formik.touched.amount && formik.errors.amount)}
                      disabled={isLoading}
                      sx={{ borderRadius: 1.75, fontSize: '0.8125rem', height: 38 }}
                    />
                    {formik.touched.amount && formik.errors.amount && (
                      <FormHelperText error sx={{ ml: 0.5, mt: 0.25, fontSize: '0.6875rem' }}>
                        {formik.errors.amount}
                      </FormHelperText>
                    )}

                    {/* Preset Amount Chips */}
                    <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center', mr: 0.25, fontWeight: 600, fontSize: '0.6875rem' }}>
                        Quick Select:
                      </Typography>
                      {PRESET_AMOUNTS.map((amt) => (
                        <Chip
                          key={amt}
                          label={`$${amt}`}
                          size="small"
                          onClick={() => formik.setFieldValue('amount', amt)}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: 24,
                            borderRadius: 1.25,
                            bgcolor: Number(formik.values.amount) === amt ? 'primary.main' : 'grey.100',
                            color: Number(formik.values.amount) === amt ? 'primary.contrastText' : 'text.primary',
                            '&:hover': {
                              bgcolor: Number(formik.values.amount) === amt ? 'primary.dark' : 'grey.200',
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Description Field */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Description & Learning Objective *
                    </Typography>
                    <OutlinedInput
                      fullWidth
                      multiline
                      rows={3}
                      name="description"
                      placeholder="Describe what happened and how the scholar must budget to resolve this expense..."
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(formik.touched.description && formik.errors.description)}
                      disabled={isLoading}
                      sx={{ borderRadius: 1.75, fontSize: '0.8125rem' }}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <FormHelperText error sx={{ ml: 0.5, mt: 0.25, fontSize: '0.6875rem' }}>
                        {formik.errors.description}
                      </FormHelperText>
                    )}
                  </Box>
                </Stack>

                {/* Action Buttons */}
                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(-1)}
                    disabled={isLoading}
                    sx={{
                      borderRadius: 1.75,
                      px: 2,
                      height: 36,
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      color: 'text.secondary',
                      borderColor: 'divider',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={isLoading || !formik.values.title || !formik.values.description || !formik.values.amount}
                    sx={{
                      borderRadius: 1.75,
                      px: 2.5,
                      height: 36,
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      bgcolor: 'primary.main',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    {isLoading ? 'Creating Scenario...' : 'Create Scenario'}
                  </Button>
                </Stack>
              </Card>
            </Grid>

            {/* Live Whammy Card Preview */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  bgcolor: 'grey.50',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                  <Icon icon="solar:eye-bold-duotone" width={16} style={{ color: '#D97706' }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                    Scholar Card Preview
                  </Typography>
                </Stack>

                <Card
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px dashed',
                    borderColor: formik.values.title ? 'primary.main' : 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(237, 180, 46, 0.15)',
                        color: '#D97706',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="solar:danger-bold-duotone" width={18} />
                    </Box>
                    <Chip
                      label="Whammy Event"
                      size="small"
                      sx={{
                        height: 20,
                        fontWeight: 800,
                        fontSize: '0.6875rem',
                        bgcolor: 'rgba(239, 68, 68, 0.12)',
                        color: '#DC2626',
                        borderRadius: 1.25,
                      }}
                    />
                  </Stack>

                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5, fontSize: '0.875rem' }}>
                    {formik.values.title || 'Scenario Title'}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                      display: 'block',
                      minHeight: 36,
                      lineHeight: 1.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    {formik.values.description || 'Provide a description to see how it will read on the student simulation card...'}
                  </Typography>

                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                      Penalty Due
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#DC2626', fontSize: '1.1rem' }}>
                      ${currentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Card>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Page>
  );
}
